import { useEffect, useState } from "react";
import { CHULA } from "@chula/shared";
import type { AcademicSnapshot, FallbackTier } from "@chula/shared";
import { useTheme } from "../hooks/useTheme";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenSheets: () => void;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export function TopBar({ feeds, onOpenCatalog, catalogCount, viewMode, onCycleViewMode, onOpenManual, onOpenSheets, sheetsConfigured, academic }: Props) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const { theme, toggle } = useTheme();
  const liveCount = feeds.filter((f) => f.tier === "live").length;

  return (
    <header className="topbar">
      {/* Sponsor + identity bar */}
      <div className="brand">
        <a
          className="sponsor"
          href="https://slic-index.nonarkara.org"
          target="_blank"
          rel="noreferrer"
          aria-label="SLIC Index — Smart Liveable Cities"
        >
          <img src="/logos/slic.png" alt="SLIC" />
        </a>
        <a
          className="sponsor"
          href="https://www.depa.or.th"
          target="_blank"
          rel="noreferrer"
          aria-label="depa — Digital Economy Promotion Agency"
        >
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <span className="brand-sep" aria-hidden />
        <div className="brand-stack">
          <strong>Chula Control Tower</strong>
          <span className="brand-sub mono">
            CCT-01 · {CHULA.name.en} · Bangkok
          </span>
        </div>
      </div>

      {/* Live feed chips */}
      <div className="feeds">
        <span className="feeds-label">FEEDS</span>
        {feeds.map((f) => (
          <span
            key={f.label}
            className={`feed-chip feed-${f.tier}`}
            title={`${f.tier} · ${f.ageMinutes}m ago`}
          >
            <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
            {f.label}
          </span>
        ))}
      </div>

      <div className="topbar-right">
        <span className="live-count mono">
          <span className="dot live" style={{ marginRight: 5 }} />
          {liveCount}/{feeds.length} LIVE
        </span>
        <button
          onClick={onOpenSheets}
          className={`mono sheets-btn ${sheetsConfigured ? "sheets-btn-live" : ""}`}
          aria-label={sheetsConfigured ? "Open Google Sheets live feed" : "Set up Google Sheets integration"}
          title={sheetsConfigured ? "Open the live-data Google Sheet" : "Connect a Google Sheet to this dashboard"}
        >
          {sheetsConfigured ? "▦ SHEETS" : "▦ SHEETS"}
        </button>
        <button onClick={onOpenCatalog} className="mono" aria-label="Open source catalog">
          SOURCES · {catalogCount}
        </button>
        <button
          onClick={onOpenManual}
          className="mono manual-trigger"
          aria-label="Open user manual"
          title="Manual — every button, color, acronym"
        >
          ?
        </button>
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3D substructure`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS · transparent superstructure, show underground"
                : "Tap → 2D · top-down"
          }
        >
          {viewMode}
        </button>
        <button
          onClick={toggle}
          className="theme-toggle"
          aria-label={`Theme: ${theme}. Click to cycle dark → light → high-contrast`}
          title={theme === "dark" ? "Switch to light theme" : theme === "light" ? "Switch to high-contrast theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? "☀" : theme === "light" ? "◑" : "◆"}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
}
